import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { RULES, CODE_LABELS, rulesFor } from "@/lib/facade_rules";

/**
 * FacadeBadges - one small ribbon per side of the plot indicating the
 * context of that facade: 🛣️ street / 🧱 neighbor (party wall) / 🌤️ open
 * air. Tapping/clicking any badge opens a small overlay that shows the
 * code clause + minimum offset applicable to that facade type, so users
 * understand WHY a neighbor facade cannot host openings.
 *
 * The pixel-space projection is done by the parent (each surface uses a
 * different scale/offset), so the parent supplies `px(m) / py(m) / w / h`.
 */

const STYLE = {
  street:   { fill: "#16A34A", stroke: "#166534", ar: "🛣️ الشارع (الواجهة)", en: "🛣️ Street (facade)" },
  neighbor: { fill: "#D97706", stroke: "#92400E", ar: "🧱 جار (لا فتحات)",     en: "🧱 Neighbor (no openings)" },
  open:     { fill: "#0EA5E9", stroke: "#0369A1", ar: "🌤️ هواء طلق",           en: "🌤️ Open air" },
};

function Badge({ cx, cy, rotate, kind, testId, onClick, lang }) {
  const s = STYLE[kind] || STYLE.open;
  const t = lang === "ar" ? s.ar : s.en;
  const bw = Math.max(90, t.length * 6.6);
  const bh = 15;
  const tr = rotate ? `rotate(${rotate} ${cx} ${cy})` : "";
  return (
    <g transform={tr} data-testid={testId} onClick={onClick} style={{ cursor: "pointer" }}>
      <rect x={cx - bw / 2} y={cy - bh / 2} width={bw} height={bh} rx={7} ry={7}
            fill={s.fill} fillOpacity={0.95} stroke={s.stroke} strokeWidth={0.8} />
      <text x={cx} y={cy + 3.5} fontSize={8.5} fontWeight="800" textAnchor="middle" fill="#ffffff"
            style={{ paintOrder: "stroke", stroke: s.stroke, strokeWidth: 0.5 }}>{t}</text>
    </g>
  );
}

/**
 * Render 4 badges (front/rear/left/right).
 *
 * Props:
 *  - meta: { front, rear, left, right }
 *  - positions: { front:{cx,cy}, rear:{cx,cy}, left:{cx,cy}, right:{cx,cy} }
 */
export default function FacadeBadges({ meta = {}, positions, testIdPrefix = "facade-badge", designCode = "SBC_304" }) {
  const { lang } = useAuth();
  const [openKind, setOpenKind] = useState(null);
  const [pickedCode, setPickedCode] = useState(designCode || "SBC_304");
  const sides = [
    { key: "front", rotate: 0,   ...positions.front, kind: meta.front || "street" },
    { key: "rear",  rotate: 0,   ...positions.rear,  kind: meta.rear  || "open"   },
    { key: "left",  rotate: -90, ...positions.left,  kind: meta.left  || "open"   },
    { key: "right", rotate: 90,  ...positions.right, kind: meta.right || "open"   },
  ];
  const rule = openKind ? rulesFor(pickedCode, openKind, lang) : null;
  return (
    <>
      <g>
        {sides.map((sd) => (
          <Badge key={sd.key} cx={sd.cx} cy={sd.cy} rotate={sd.rotate}
                 kind={sd.kind}
                 testId={`${testIdPrefix}-${sd.key}`}
                 onClick={() => { setOpenKind(sd.kind); setPickedCode(designCode || "SBC_304"); }}
                 lang={lang} />
        ))}
      </g>
      {openKind && rule && (
        <foreignObject x="1%" y="1%" width="98%" height="98%" data-testid="facade-rules-overlay">
          <div xmlns="http://www.w3.org/1999/xhtml"
               style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              background: "#0F172A", color: "#F8FAFC", padding: "18px 22px",
              borderRadius: 14, border: `2px solid ${STYLE[openKind].stroke}`, maxWidth: 520,
              boxShadow: "0 20px 40px rgba(0,0,0,0.6)", direction: lang === "ar" ? "rtl" : "ltr",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontWeight: 900, fontSize: 15, color: STYLE[openKind].fill }}>
                  {rule.title}
                </div>
                <button onClick={() => setOpenKind(null)}
                        data-testid="facade-rules-close"
                        style={{ background: "transparent", color: "#F8FAFC", border: "none", fontSize: 20, cursor: "pointer" }}>
                  ×
                </button>
              </div>

              {/* Code picker - tap a code to see how the rules for this
                  facade kind change per design code (SBC vs ACI vs Eurocode
                  vs BS vs IS/NBC vs ECP). Teaches the practical impact of
                  the design code choice. */}
              <div data-testid="facade-rules-code-picker"
                   style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12,
                            paddingBottom: 8, borderBottom: "1px solid rgba(148,163,184,0.25)" }}>
                {Object.keys(RULES).map((code) => (
                  <button key={code}
                          data-testid={`facade-rules-code-${code}`}
                          onClick={() => setPickedCode(code)}
                          style={{
                            border: `1px solid ${pickedCode === code ? STYLE[openKind].fill : "rgba(148,163,184,0.35)"}`,
                            background: pickedCode === code ? STYLE[openKind].fill : "transparent",
                            color: pickedCode === code ? "#ffffff" : "#CBD5E1",
                            borderRadius: 999, padding: "3px 10px", fontSize: 10.5,
                            fontWeight: 700, cursor: "pointer",
                          }}>
                    {CODE_LABELS[code][lang === "ar" ? "ar" : "en"]}
                  </button>
                ))}
              </div>

              {rule.lines.map((l, i) => (
                <div key={i} style={{ fontSize: 12, lineHeight: 1.7 }}>{l}</div>
              ))}
              <div style={{
                marginTop: 10, paddingTop: 8, borderTop: "1px solid rgba(148,163,184,0.3)",
                fontSize: 10, color: "#94A3B8",
              }}>
                {lang === "ar" ? "المراجع: " : "References: "}
                {rule.refs.join(" · ")}
              </div>
            </div>
          </div>
        </foreignObject>
      )}
    </>
  );
}
